import anime from 'animejs';
import { Phone } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

const SUPPORT_PHONE = import.meta.env.VITE_SUPPORT_PHONE_NUMBER ?? '';

export function PhoneBanner() {
  const { t } = useTranslation();
  const bannerRef = useRef<HTMLDivElement>(null);
  const iconRef = useRef<HTMLDivElement>(null);
  const pulseRingRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!SUPPORT_PHONE || !bannerRef.current || !iconRef.current) return;

    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!prefersReduced) {
      anime({
        targets: bannerRef.current,
        translateY: [100, 0],
        opacity: [0, 1],
        duration: 600,
        easing: 'easeOutCubic',
      });

      anime({
        targets: iconRef.current,
        scale: [1, 1.15, 1],
        duration: 1200,
        easing: 'easeInOutSine',
        loop: true,
      });

      if (pulseRingRef.current) {
        anime({
          targets: pulseRingRef.current,
          scale: [1, 1.8],
          opacity: [0.6, 0],
          duration: 1500,
          easing: 'easeOutCubic',
          loop: true,
        });
      }
    }
  }, []);

  if (!SUPPORT_PHONE) return null;

  return (
    <div
      ref={bannerRef}
      className="fixed bottom-0 left-0 right-0 z-50 flex items-center justify-center gap-3 bg-primary px-4 py-3 text-primary-foreground shadow-[0_-2px_10px_rgba(0,0,0,0.1)] opacity-0 md:bottom-4 md:left-4 md:right-auto md:rounded-full md:px-6 md:py-2.5"
    >
      <div className="relative flex items-center justify-center">
        <span
          ref={pulseRingRef}
          className="absolute inline-flex h-8 w-8 rounded-full bg-primary-foreground/30"
        />
        <div
          ref={iconRef}
          className="relative flex h-8 w-8 items-center justify-center rounded-full bg-primary-foreground/20"
        >
          <Phone className="h-4 w-4 text-primary-foreground" />
        </div>
      </div>

      <div className="flex items-center gap-2">
        <span className="text-sm font-medium">{t('Feedback.phone.label')}</span>
        <span className="rounded-full bg-primary-foreground/20 px-3 py-1 text-sm font-semibold">
          {SUPPORT_PHONE}
        </span>
      </div>
    </div>
  );
}
